import { useState, useEffect, useCallback, useMemo } from "react";
import { message } from "antd";
import Service from "../service";

/**
 * Server-backed column visibility for a listing page's Table. The saved
 * selection is scoped to the logged-in user (so it follows them across
 * logins/devices) and keyed by `pageKey`, one GET on mount / one PUT per
 * explicit save - no polling, no per-toggle writes.
 *
 * @param {string} pageKey unique per listing page, e.g. "employeeMasterList"
 * @param {Array} columns full catalog of toggleable antd Table columns (each needs a `key` or `dataIndex`)
 * @param {string[]} defaultHiddenKeys column keys hidden until the user saves a selection
 * @param {string[]} alwaysVisibleKeys column keys that must always render - a stale saved
 *   preference (from before a column was locked, or a column ColumnSelector no longer offers
 *   a checkbox for) can never hide these, unlike ColumnSelector's own `alwaysVisibleKeys` prop,
 *   which only stops a user from unchecking a box that's actually shown.
 */
const useColumnVisibility = (pageKey, columns, defaultHiddenKeys = [], alwaysVisibleKeys = []) => {
  const allKeys = useMemo(
    () => columns.map((col) => col.key || col.dataIndex).filter(Boolean),
    [columns]
  );

  const defaultKeys = useMemo(
    () => allKeys.filter((k) => !defaultHiddenKeys.includes(k) || alwaysVisibleKeys.includes(k)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allKeys, defaultHiddenKeys.join(","), alwaysVisibleKeys.join(",")]
  );

  const [visibleKeys, setVisibleKeys] = useState(defaultKeys);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchPreference = async () => {
      try {
        const response = await Service.makeAPICall({
          methodName: Service.getMethod,
          api_url: `${Service.columnPreference}/${pageKey}`,
        });
        const saved = response?.data?.data?.visibleColumns;
        if (isMounted && Array.isArray(saved) && saved.length > 0) {
          setVisibleKeys(
            allKeys.filter((k) => saved.includes(k) || alwaysVisibleKeys.includes(k))
          );
        }
      } catch (error) {
        // column visibility is a convenience, not a critical path - fall back to defaults silently
      } finally {
        if (isMounted) setLoaded(true);
      }
    };

    fetchPreference();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  const saveVisibleKeys = useCallback(
    async (newKeys) => {
      setSaving(true);
      try {
        // makeAPICall resolves even on a server error response (it only rejects
        // on a network failure), so success has to be read off the payload.
        const response = await Service.makeAPICall({
          methodName: Service.putMethod,
          api_url: `${Service.columnPreference}/${pageKey}`,
          body: { visibleColumns: newKeys },
        });
        if (response?.data?.status === 200) {
          setVisibleKeys(newKeys);
          message.success("Column preference saved");
        } else {
          message.error(response?.data?.message || "Could not save column preference");
        }
      } catch (error) {
        message.error("Could not save column preference");
      } finally {
        setSaving(false);
      }
    },
    [pageKey]
  );

  const filteredColumns = useMemo(
    () =>
      columns.filter((col) => {
        const colKey = col.key || col.dataIndex;
        return !colKey || visibleKeys.includes(colKey) || alwaysVisibleKeys.includes(colKey);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, visibleKeys, alwaysVisibleKeys.join(",")]
  );

  return { visibleKeys, saveVisibleKeys, filteredColumns, loaded, saving };
};

export default useColumnVisibility;
